import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { ComprehensiveStockAnalysis } from '@/types/perplexity.types';
import { SkeletonCard } from './SkeletonCard';

interface RisksCatalystsCardProps {
  data: ComprehensiveStockAnalysis['risksAndCatalysts'] | null | undefined;
  isLoading: boolean;
}

export const RisksCatalystsCard: React.FC<RisksCatalystsCardProps> = ({ data, isLoading }) => {
  if (isLoading || !data) {
    return <SkeletonCard lines={6} />;
  }

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Ionicons name="flash" size={20} color={Colors.primary} />
        <Text style={styles.cardTitle}>Risks & Catalysts</Text>
      </View>

      <View style={styles.risksRow}>
        {/* Catalysts (Upside) */}
        <View style={styles.risksColumn}>
          <View style={styles.risksHeader}>
            <Ionicons name="trending-up" size={16} color="#34C759" />
            <Text style={[styles.risksHeaderText, { color: '#34C759' }]}>Upside</Text>
          </View>
          {data.catalysts.map((catalyst, index) => (
            <View key={index} style={styles.riskItem}>
              <Text style={[styles.riskBullet, { color: '#34C759' }]}>•</Text>
              <Text style={styles.riskText}>{catalyst}</Text>
            </View>
          ))}
        </View>

        {/* Risks (Downside) */}
        <View style={styles.risksColumn}>
          <View style={styles.risksHeader}>
            <Ionicons name="trending-down" size={16} color="#FF3B30" />
            <Text style={[styles.risksHeaderText, { color: '#FF3B30' }]}>Downside</Text>
          </View>
          {data.risks.map((risk, index) => (
            <View key={index} style={styles.riskItem}>
              <Text style={[styles.riskBullet, { color: '#FF3B30' }]}>•</Text>
              <Text style={styles.riskText}>{risk}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    flex: 1,
  },
  risksRow: {
    flexDirection: 'row',
    gap: 12,
  },
  risksColumn: {
    flex: 1,
  },
  risksHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 8,
  },
  risksHeaderText: {
    fontSize: 14,
    fontWeight: '700',
  },
  riskItem: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  riskBullet: {
    fontSize: 14,
    marginRight: 6,
    marginTop: 2,
  },
  riskText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    color: Colors.text.primary,
  },
});
